// E8CubeSurfaceAreaVolumeClosedTask.js
// Zadanie zamknięte E8 (2025 CZE zad. 13) - pole powierzchni sześcianu → objętość.

import React from "react";
import { BlockMath, ChoiceOptionMath, InlineMath } from "../../../../components/math/KatexWrappers";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];
const RANDOM_SURFACES = [24, 54, 96, 216, 294, 384, 486, 600]; // a = 2, 3, 4, 6, 7, 8, 9, 10

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function buildData({ surface, fixedOptions = null }) {
  const a = Math.round(Math.sqrt(surface / 6));
  const volume = a * a * a;
  let options, correctIndex;
  if (fixedOptions) {
    options = fixedOptions;
    correctIndex = fixedOptions.findIndex((o) => o.isCorrect);
  } else {
    const pool = shuffle([
      { value: a * a },
      { value: volume, isCorrect: true },
      { value: a * a * a * a },
      { value: surface * a },
    ]);
    options = pool;
    correctIndex = pool.findIndex((o) => o.isCorrect);
  }
  return { surface, a, volume, options, correctIndex };
}

function createOriginalTask() {
  return {
    type: "E8CubeSurfaceAreaVolumeClosedTask",
    data: buildData({
      surface: 150,
      fixedOptions: [
        { value: 25 },
        { value: 125, isCorrect: true },
        { value: 625 },
        { value: 750 },
      ],
    }),
    source: { variant: "original", exam: "E8", session: "2025_CZE", taskNumber: 13 },
    tags: ["e8", "stereometria", "szescian", "pole-powierzchni", "objetosc"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();
  return {
    type: "E8CubeSurfaceAreaVolumeClosedTask",
    data: buildData({ surface: pick(RANDOM_SURFACES) }),
    source: { variant: "random", basedOn: "E8_2025_CZE_13" },
    tags: ["e8", "stereometria", "szescian", "pole-powierzchni", "objetosc"],
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        Pole powierzchni całkowitej sześcianu jest równe{" "}
        <InlineMath math={`${data.surface}\\text{ cm}^2`} />.
      </p>
      <p>
        <strong>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</strong>
      </p>
      <p>Objętość tego sześcianu jest równa</p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 10 }}>
        {data.options.map((option, index) => (
          <div key={index}>
            <ChoiceOptionMath letter={LETTERS[index]} math={`${option.value}\\text{ cm}^3`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  return (
    <ABCDInput
      options={data.options.map((_, index) => ({ label: LETTERS[index], textNode: LETTERS[index] }))}
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  return userAnswer === data.correctIndex;
}

export function renderAnswer({ data }) {
  return (
    <ChoiceOptionMath
      letter={LETTERS[data.correctIndex]}
      math={`${data.volume}\\text{ cm}^3`}
    />
  );
}

export function renderUserAnswer(userAnswer, { data }) {
  if (typeof userAnswer !== "number") return <i>(brak odpowiedzi)</i>;
  const option = data.options[userAnswer];
  if (!option) return <i>(nieprawidłowy wybór)</i>;
  return <ChoiceOptionMath letter={LETTERS[userAnswer]} math={`${option.value}\\text{ cm}^3`} />;
}

export function renderExplanation({ data }) {
  const { surface, a, volume } = data;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p>
        <strong>Krok 1.</strong> Sześcian ma <InlineMath math="6" /> ścian kwadratowych,
        każda o boku <InlineMath math="a" />. Z pola powierzchni wyznaczamy krawędź:
      </p>
      <BlockMath
        math={`6a^2=${surface}\\implies a^2=${a * a}\\implies a=${a}\\text{ cm}`}
      />
      <p>
        <strong>Krok 2.</strong> Obliczamy objętość sześcianu:
      </p>
      <BlockMath math={`V=a^3=${a}^3=${volume}\\text{ cm}^3`} />
      <p>
        Poprawna odpowiedź to{" "}
        <InlineMath math={`\\text{${LETTERS[data.correctIndex]}}`} />.
      </p>
    </div>
  );
}
